import { Navigate, Route } from 'react-router-dom';
import { apiDocsPlugin, ApiExplorerPage } from '@backstage/plugin-api-docs';
import {
  CatalogEntityPage,
  CatalogIndexPage,
  catalogPlugin,
} from '@backstage/plugin-catalog';
import {
  CatalogImportPage,
  catalogImportPlugin,
} from '@backstage/plugin-catalog-import';
import { ScaffolderPage, scaffolderPlugin } from '@backstage/plugin-scaffolder';
import { orgPlugin } from '@backstage/plugin-org';
import { SearchPage } from '@backstage/plugin-search';
import {
  TechDocsIndexPage,
  techdocsPlugin,
  TechDocsReaderPage,
} from '@backstage/plugin-techdocs';
import { TechDocsAddons } from '@backstage/plugin-techdocs-react';
import { ReportIssue } from '@backstage/plugin-techdocs-module-addons-contrib';
import { UserSettingsPage } from '@backstage/plugin-user-settings';
import { apis, keycloakAuthApiRef } from './apis';
import { entityPage } from './components/catalog/EntityPage';
import { searchPage } from './components/search/SearchPage';
import { Root } from './components/Root';

import {
  AlertDisplay,
  OAuthRequestDialog,
  SignInPage,
} from '@backstage/core-components';
import { createApp } from '@backstage/app-defaults';
import { UnifiedThemeProvider } from '@backstage/theme';
import LightIcon from '@material-ui/icons/WbSunny';
import DarkIcon from '@material-ui/icons/Brightness2';
import { portalDarkTheme, portalLightTheme } from './theme';
import { AppRouter, FlatRoutes } from '@backstage/core-app-api';
import { CatalogGraphPage } from '@backstage/plugin-catalog-graph';
import { RequirePermission } from '@backstage/plugin-permission-react';
import { catalogEntityCreatePermission } from '@backstage/plugin-catalog-common/alpha';
import { NotificationsPage } from '@backstage/plugin-notifications';
import { SignalsDisplay } from '@backstage/plugin-signals';
import { SqlConsolePage } from './components/sql-console/SqlConsolePage';
import { DataHubPage } from './components/data-hub/DataHubPage';
import { RegistryPage } from './components/registry/RegistryPage';
import { HomePage } from './components/home/HomePage';
import { MenuRoute, RootRedirect } from './components/portal-access/MenuRoute';
import { AccessApprovalPage } from './components/portal-access/AccessApprovalPage';
import { AccessRequestPage } from './components/portal-access/AccessRequestPage';

/*
 * scaffolder 필드 extension 이 없다.
 *
 * SQL 파이프라인 생성은 `sql-pipeline` 플러그인의 다이얼로그가 한다 —
 * 새 DAG 규약은 파일 3 개를 만들고 날짜 식·DDL·task 키가 서로 맞물려야 해서
 * (schedule 주기가 삭제 쿼리의 날짜 식을 정하고, 생성 컬럼이 INSERT 컬럼과
 * 같아야 한다) JSON Schema 폼으로는 교차 검증을 표현할 수 없다.
 * scaffolder 는 소프트웨어 카탈로그 쪽 템플릿을 위해 남겨 둔다.
 */

const app = createApp({
  apis,
  bindRoutes({ bind }) {
    bind(catalogPlugin.externalRoutes, {
      createComponent: scaffolderPlugin.routes.root,
      viewTechDoc: techdocsPlugin.routes.docRoot,
      createFromTemplate: scaffolderPlugin.routes.selectedTemplate,
    });
    bind(apiDocsPlugin.externalRoutes, {
      registerApi: catalogImportPlugin.routes.importPage,
    });
    bind(scaffolderPlugin.externalRoutes, {
      registerComponent: catalogImportPlugin.routes.importPage,
      viewTechDoc: techdocsPlugin.routes.docRoot,
    });
    bind(orgPlugin.externalRoutes, {
      catalogIndex: catalogPlugin.routes.catalogIndex,
    });
  },
  themes: [
    {
      id: 'light',
      title: 'Light Theme',
      variant: 'light',
      icon: <LightIcon />,
      Provider: ({ children }) => (
        <UnifiedThemeProvider theme={portalLightTheme}>
          {children}
        </UnifiedThemeProvider>
      ),
    },
    {
      id: 'dark',
      title: 'Dark Theme',
      variant: 'dark',
      icon: <DarkIcon />,
      Provider: ({ children }) => (
        <UnifiedThemeProvider theme={portalDarkTheme}>
          {children}
        </UnifiedThemeProvider>
      ),
    },
  ],
  components: {
    /*
      로그인은 Keycloak 하나다. Google 은 **sign-in 목록에 두지 않는다** —
      SQL 콘솔이 BigQuery 토큰을 받을 때만 쓰이고, 그때 `googleAuthApi` 가
      동의 창을 띄운다. 로그인 선택지로 두면 Google 로 로그인한 사용자는
      Keycloak 그룹이 없어 권한 0 이 되는데, 화면상 구분이 안 되어 "왜 어떤
      사람은 안 되는가" 가 된다.
    */
    SignInPage: props => (
      <SignInPage
        {...props}
        auto
        provider={{
          id: 'keycloak-auth-provider',
          title: 'Keycloak',
          message: 'Keycloak 계정으로 로그인',
          apiRef: keycloakAuthApiRef,
        }}
      />
    ),
  },
});

const routes = (
  <FlatRoutes>
    <Route path="/" element={<RootRedirect />} />
    <Route path="/home" element={<HomePage />} />
    <Route
      path="/catalog"
      element={
        <MenuRoute menu="menu.catalog">
          <CatalogIndexPage actions={[]} />
        </MenuRoute>
      }
    />
    <Route
      path="/catalog/:namespace/:kind/:name"
      element={
        <MenuRoute menu="menu.catalog">
          <CatalogEntityPage />
        </MenuRoute>
      }
    >
      {entityPage}
    </Route>
    <Route
      path="/docs"
      element={
        <MenuRoute menu="menu.catalog">
          <TechDocsIndexPage />
        </MenuRoute>
      }
    />
    <Route
      path="/docs/:namespace/:kind/:name/*"
      element={
        <MenuRoute menu="menu.catalog">
          <TechDocsReaderPage />
        </MenuRoute>
      }
    >
      <TechDocsAddons>
        <ReportIssue />
      </TechDocsAddons>
    </Route>
    <Route
      path="/create"
      element={
        <MenuRoute menu="menu.catalog">
          <ScaffolderPage />
        </MenuRoute>
      }
    />
    <Route
      path="/api-docs"
      element={
        <MenuRoute menu="menu.catalog">
          <ApiExplorerPage />
        </MenuRoute>
      }
    />
    <Route
      path="/catalog-import"
      element={
        <MenuRoute menu="menu.catalog">
          <RequirePermission permission={catalogEntityCreatePermission}>
            <CatalogImportPage />
          </RequirePermission>
        </MenuRoute>
      }
    />
    <Route
      path="/search"
      element={
        <MenuRoute menu="menu.catalog">
          <SearchPage />
        </MenuRoute>
      }
    >
      {searchPage}
    </Route>
    <Route path="/settings" element={<UserSettingsPage />} />
    {/* 로그인 전원 — 화면 노출은 힌트이고 승인·회수 권한은 서버가 판정한다 */}
    <Route path="/access-request" element={<AccessRequestPage />} />
    <Route path="/access-approval" element={<AccessApprovalPage />} />
    <Route
      path="/catalog-graph"
      element={
        <MenuRoute menu="menu.catalog">
          <CatalogGraphPage />
        </MenuRoute>
      }
    />
    <Route
      path="/sql-console"
      element={
        <MenuRoute menu="menu.sql-console">
          <SqlConsolePage />
        </MenuRoute>
      }
    />
    <Route
      path="/data-hub"
      element={
        <MenuRoute menu="menu.data-hub">
          <DataHubPage />
        </MenuRoute>
      }
    />
    <Route
      path="/data-hub/*"
      element={
        <MenuRoute menu="menu.data-hub">
          <DataHubPage />
        </MenuRoute>
      }
    />
    {/* 구 Data Hub 탭 경로 북마크 호환 */}
    <Route
      path="/data-hub/registry"
      element={<Navigate to="/registry" replace />}
    />
    <Route
      path="/data-hub/manifest-review"
      element={<Navigate to="/registry/manifest-review" replace />}
    />
    <Route
      path="/registry"
      element={
        <MenuRoute menu="menu.registry">
          <RegistryPage />
        </MenuRoute>
      }
    />
    <Route
      path="/registry/*"
      element={
        <MenuRoute menu="menu.registry">
          <RegistryPage />
        </MenuRoute>
      }
    />
    <Route
      path="/notifications"
      element={
        <MenuRoute menu="menu.catalog">
          <NotificationsPage />
        </MenuRoute>
      }
    />
  </FlatRoutes>
);

export default app.createRoot(
  <>
    <AlertDisplay />
    <OAuthRequestDialog />
    <SignalsDisplay />
    <AppRouter>
      <Root>{routes}</Root>
    </AppRouter>
  </>,
);
